import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Compass, Camera, Mountain, Sunrise, Sunset, Star, ShieldCheck, Clock } from "lucide-react";
import BookingWidget from "@/components/BookingWidget";
import { api, inr } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HotelCard } from "@/pages/Hotels";
import { PackageCard } from "@/pages/Packages";
import { useContent } from "@/lib/content";
import { Seo } from "@/components/Seo";
import { ReviewsSection } from "@/components/Reviews";
import { LiveSlots } from "@/components/LiveSlots";
import { HeroDepartures } from "@/components/HeroDepartures";
import { StaysSearch } from "@/components/StaysSearch";
import { BedDouble, Binoculars } from "lucide-react";

function HeroSearch() {
  const [tab, setTab] = useState("safari");
  const tabs = [["safari", "Safari", Binoculars], ["stays", "Stays", BedDouble]];
  return (
    <div data-testid="hero-search">
      <div className="flex gap-1">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`hero-tab-${k}`}
            className={`flex items-center gap-2 px-5 py-3 rounded-t-xl text-sm font-medium transition-colors duration-200 ${tab === k ? "bg-white/95 text-[#1C2421]" : "bg-black/30 text-white hover:bg-black/45 backdrop-blur"}`}>
            <Icon size={16}/>{label}
          </button>
        ))}
      </div>
      <div className="[&>div]:rounded-tl-none [&>div]:lg:min-h-[260px]">{tab === "safari" ? <BookingWidget/> : <StaysSearch/>}</div>
    </div>
  );
}

const TRUST_ICONS = [ShieldCheck, Star, Clock, Compass];
const CAT_ICON = { private: Compass, shared: Compass, sunrise: Sunrise, sunset: Sunset, jungle: Mountain, photography: Camera, custom: Star };

export default function Home() {
  const [safaris, setSafaris] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => {
    api.get("/safaris").then((r) => setSafaris(r.data));
    api.get("/hotels").then((r) => setHotels(r.data));
    api.get("/packages").then((r) => setPkgs(r.data));
  }, []);
  const c = useContent("home");
  const [rev, setRev] = useState(null);
  if (!c) return <div className="min-h-[90vh] bg-[#0D1410]"/>;
  const { hero, sections: sec, steps, destination: dest, faq } = c;

  return (
    <div>
      <Seo title={c.seo.meta_title} description={c.seo.meta_description} image={c.seo.og_image || hero.image}
        jsonLd={homeLd(faq, rev)}/>
      {/* HERO */}
      <section className="relative min-h-[80vh] md:min-h-[90vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={hero.image} alt={hero.image_alt} className="w-full h-full object-cover" data-testid="home-hero-image"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D1410]/90 via-[#0D1410]/55 to-transparent" />
        </div>
        <div className="relative jt-container pb-8 md:pb-16 w-full">
          <div className="max-w-2xl text-white mb-8 md:mb-10">
            <div className="jt-eyebrow mb-4 text-[#D4A359]">{hero.eyebrow}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="home-hero-title">{hero.title}</h1>
            <p className="text-base md:text-lg text-[#E5E0D6] leading-relaxed max-w-xl" data-testid="home-hero-subtitle">{hero.subtitle}</p>
          </div>
          <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-6 lg:gap-8 items-end">
            <HeroSearch/>
            <HeroDepartures/>
          </div>
        </div>
      </section>

      <LiveSlots/>

      {/* TRUST STRIP */}
      <section className="bg-[#1E3A2B] text-[#F5F0EB] py-6">
        <div className="jt-container grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          {c.trust.map((t, i) => <TrustItem key={i} icon={TRUST_ICONS[i % 4]} title={t.title} sub={t.sub}/>)}
        </div>
      </section>

      {/* FEATURED SAFARIS */}
      <section className="jt-container py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="jt-eyebrow mb-3">{sec.safaris_eyebrow}</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A2B]">{sec.safaris_title}</h2>
          </div>
          <Link to="/safaris" className="text-sm text-[#C86D51] hover:underline" data-testid="home-view-all">View all safaris →</Link>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {safaris.slice(0, 6).map((s, i) => {
            const Icon = CAT_ICON[s.category] || Compass;
            return (
              <Link
                key={s.id}
                to={`/safaris/${s.slug}`}
                data-testid={`home-safari-card-${s.slug}`}
                className={`group relative overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 ${i % 3 === 1 ? "md:translate-y-6" : ""}`}
              >
                <div className="h-64 overflow-hidden">
                  <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transform duration-500"/>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#C86D51] mb-2">
                    <Icon size={14}/> {s.category}
                  </div>
                  <h3 className="font-serif text-xl mb-2 text-[#1C2421]">{s.name}</h3>
                  <p className="text-sm text-[#5C6B63] mb-4 line-clamp-2">{s.short_description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-[#1E3A2B]">from {inr(s.base_price)} <span className="text-[#5C6B63] text-xs">/{s.pricing_mode === "per_vehicle" ? "jeep" : "seat"}</span></span>
                    <span className="text-sm font-medium text-[#C86D51] inline-flex items-center gap-1">Explore <ArrowRight size={14}/></span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* STAYS */}
      <section className="bg-[#F5F0EB] py-20">
        <div className="jt-container">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="jt-eyebrow mb-3">{sec.stays_eyebrow}</div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A2B]">{sec.stays_title}</h2>
            </div>
            <Link to="/stays" className="text-sm text-[#C86D51] hover:underline" data-testid="home-view-stays">All stays →</Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {hotels.slice(0, 3).map((h) => <HotelCard key={h.id} h={h} testid={`home-stay-card-${h.slug}`}/>)}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="jt-container py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="jt-eyebrow mb-3">{sec.packages_eyebrow}</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A2B]">{sec.packages_title}</h2>
          </div>
          <Link to="/packages" className="text-sm text-[#C86D51] hover:underline" data-testid="home-view-packages">All packages →</Link>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {pkgs.slice(0, 2).map((p) => <PackageCard key={p.id} p={p} testid={`home-package-card-${p.slug}`}/>)}
        </div>
      </section>

      {/* HOW TO BOOK */}
      <section className="bg-[#F5F0EB] py-20">
        <div className="jt-container">
          <div className="jt-eyebrow mb-3">{steps.eyebrow}</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A2B] mb-12 max-w-xl">{steps.title}</h2>
          <div className="grid gap-6 md:grid-cols-5">
            {steps.items.map((x) => x.text).map((step, i) => (
              <div key={i} className="relative" data-testid={`how-step-${i+1}`}>
                <div className="font-display text-3xl text-[#C86D51] mb-2">0{i+1}</div>
                <div className="text-sm font-medium text-[#1C2421]">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT JAWAI */}
      <section className="jt-container py-20 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="jt-eyebrow mb-3">{dest.eyebrow}</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A2B] mb-5">{dest.title}</h2>
          <div className="jt-prose mb-6" data-testid="home-dest-body" dangerouslySetInnerHTML={{ __html: dest.body_html }}/>
          <Link to="/about" className="jt-pill-outline" data-testid="home-about-cta">Read the guide</Link>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {dest.image1 && <img src={dest.image1} alt={dest.image1_alt} loading="lazy" className="rounded-2xl aspect-[3/4] object-cover w-full"/>}
          {dest.image2 && <img src={dest.image2} alt={dest.image2_alt} loading="lazy" className="rounded-2xl aspect-[3/4] object-cover w-full translate-y-6"/>}
        </div>
      </section>

      <ReviewsSection onLoad={setRev}/>

      {/* FAQ */}
      <section className="bg-[#121A16] text-[#F5F0EB] py-20">
        <div className="jt-container max-w-3xl">
          <div className="jt-eyebrow mb-3">{faq.eyebrow}</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-8">{faq.title}</h2>
          <Accordion type="single" collapsible className="w-full">
            {faq.items.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#B3BDB6]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}

function homeLd(faq, rev) {
  const real = (rev?.reviews || []).filter((r) => !r.is_sample);
  const biz = {
    "@type": "TravelAgency", name: "JawaiTrails", url: window.location.origin, telephone: "+91-9375332211", email: "info@jawaitrails.com",
    image: `${window.location.origin}/logo-dark.png`, address: { "@type": "PostalAddress", addressLocality: "Jawai", addressRegion: "Rajasthan", addressCountry: "IN" },
  };
  if (real.length) {
    biz.aggregateRating = { "@type": "AggregateRating", ratingValue: rev.average, reviewCount: rev.count, bestRating: 5 };
    biz.review = real.slice(0, 10).map((r) => ({ "@type": "Review", author: { "@type": "Person", name: r.name }, reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 }, name: r.title, reviewBody: r.text, datePublished: r.created_at.slice(0, 10) }));
  }
  return { "@context": "https://schema.org", "@graph": [biz, { "@type": "FAQPage", mainEntity: faq.items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }] };
}

function TrustItem({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="text-[#D4A359] mt-0.5" size={20}/>
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-xs text-[#B3BDB6]">{sub}</div>
      </div>
    </div>
  );
}
